import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { isBrowserDefined } from "../../shared/services/browserService";
import { DropdownOption } from "../dropdown";

interface FixedDropdownProps {
  options: DropdownOption[];
  value: any;
  onChange: (value: any) => void;
  placeholder?: string;
  label?: string;
  className?: string;
}

const FixedDropdown: React.FC<FixedDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select an option",
  label,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const [openUpwards, setOpenUpwards] = useState(false);

  const selectedOption = options.find((option) => option.value === value);

  // Update the dropdown position whenever it's open
  const updatePosition = useCallback(() => {
    if (!isOpen || !triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;

    // Estimate dropdown height (40px per item, max 300px)
    const estimatedHeight = Math.min(300, options.length * 40 + 10);

    // Only open upwards if there's not enough space below AND there's more space above
    const shouldOpenUpwards =
      spaceBelow < estimatedHeight &&
      rect.top > spaceBelow &&
      rect.top > estimatedHeight;

    setOpenUpwards(shouldOpenUpwards);
    setCoords({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  }, [isOpen, options.length, triggerRef]);

  // Update position whenever is open
  useEffect(() => {
    if (isOpen) {
      updatePosition();
    }
  }, [isOpen, updatePosition]);

  // Set up scroll listener to update position or close dropdown
  useEffect(() => {
    if (isOpen) {
      const handleScroll = () => {
        // Update the position on scroll
        updatePosition();
      };

      window.addEventListener("scroll", handleScroll, true);
      return () => {
        window.removeEventListener("scroll", handleScroll, true);
      };
    }
  }, [isOpen, updatePosition]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isOpen &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    window.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, updatePosition]);

  const handleOptionClick = (option: DropdownOption) => {
    onChange(option.value);
    setIsOpen(false);
  };

  const renderDropdownMenu = () => {
    if (!isOpen || !isBrowserDefined() || !document.body) return null;

    // Max height for dropdown menu
    const maxDropdownHeight = Math.min(300, options.length * 40 + 10);

    // Menu styling
    const menuStyle: React.CSSProperties = {
      position: "absolute",
      width: `${coords.width}px`,
      left: `${coords.left}px`,
      zIndex: 100000,
      maxHeight: `${maxDropdownHeight}px`,
      overflowY: "auto",
      backgroundColor: "white",
      boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
      borderRadius: "0px",
      border: "1px solid #e5e7eb",
    };

    if (openUpwards) {
      menuStyle.bottom = `${window.innerHeight - coords.top + window.scrollY}px`;
    } else {
      menuStyle.top = `${coords.top}px`;
    }

    return ReactDOM.createPortal(
      <div
        ref={dropdownRef}
        style={menuStyle}
        className="dropdown-menu"
        onClick={(e) => e.stopPropagation()}
      >
        {options.map((option) => (
          <div
            key={option.id}
            className={`px-4 py-2 cursor-pointer hover:bg-black/5 transition-colors
              ${option.value === value ? "bg-black/10 font-medium" : ""}`}
            onClick={() => handleOptionClick(option)}
          >
            {option.label}
          </div>
        ))}
        {options.length === 0 && (
          <div className="px-4 py-2 text-gray-500">No options available</div>
        )}
      </div>,
      document.body,
    );
  };

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="block text-gray-700 font-medium mb-2">{label}</label>
      )}

      <div
        ref={triggerRef}
        className="relative w-full py-2 px-4 border border-gray-200 cursor-pointer bg-white"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex justify-between items-center">
          <div className={!selectedOption ? "text-gray-400" : "text-gray-800"}>
            {selectedOption ? selectedOption.label : placeholder}
          </div>
          <div className="ml-2">
            <svg
              className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
              width="12"
              height="6"
              viewBox="0 0 12 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M6 6L0 0H12L6 6Z" fill="#6B7280" />
            </svg>
          </div>
        </div>
      </div>

      {renderDropdownMenu()}
    </div>
  );
};

export default FixedDropdown;
